import { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import proxy from '@/services/ProxyService';
import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import UserSelector from '@/components/selectors/UserSelector';

const TanssCrmSettingsForm = ({
  app,
  deployment,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
  data,
}) => {
  const [displayNameFormat, setDisplayNameFormat] = useState(
    deployment?.config?.crm?.tanssDisplayName ?? 'firstName lastName',
  );
  const [tanssCategories, setTanssCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [mappings, setMappings] = useState([{ user: null, value: '' }]);

  useEffect(() => {
    const initData = async () => {
      try {
        const response = await proxy.get(
          `${data?.tanssInterface || deployment?.config?.interfaces?.tanss}/companyCategories`,
        );
        const content = response.data.content;
        setTanssCategories(content);

        const existingConfig = deployment?.config?.crm?.tanssCategories;
        if (existingConfig) {
          const catId = Object.keys(existingConfig)[0];
          if (catId) {
            setSelectedCategoryId(catId);
            const mapObj = existingConfig[catId].map || {};

            const userResponse = await tenant.get(`/user`);
            const allUsers = userResponse.data;

            const initialMappings = Object.entries(mapObj).map(
              ([userId, tanssTypeId]) => ({
                user: allUsers.find((u) => u._id === userId) || {
                  _id: userId,
                  firstName: 'Unbekannter',
                  lastName: 'User',
                },
                value: tanssTypeId,
              }),
            );

            setMappings([...initialMappings, { user: null, value: '' }]);
          }
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Daten');
      }
    };

    initData();
  }, []);

  const availableTypes =
    tanssCategories.find((cat) => String(cat.id) === String(selectedCategoryId))
      ?.types || [];

  const handleCategoryChange = (event) => {
    setSelectedCategoryId(event.target.value);
    setMappings([{ user: null, value: '' }]);
  };

  const updateMapping = (index, field, value) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;

    if (
      index === newMappings.length - 1 &&
      newMappings[index].user !== null &&
      newMappings[index].value !== ''
    ) {
      newMappings.push({ user: null, value: '' });
    }

    setMappings(newMappings);
  };

  const removeMapping = (index) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    if (newMappings.length === 0) {
      newMappings.push({ user: null, value: '' });
    }
    setMappings(newMappings);
  };

  const handleSave = async () => {
    const finalMap = mappings.reduce((acc, curr) => {
      if (curr.user?._id && curr.value !== '') {
        acc[curr.user._id] = curr.value;
      }
      return acc;
    }, {});

    const tanssCategoriesPayload = {
      [selectedCategoryId]: {
        field: 'responsibleUserId',
        map: finalMap,
      },
    };
    console.log(tanssCategoriesPayload);

    next({
      tanssDisplayName: displayNameFormat,
      tanssCategories: tanssCategoriesPayload,
    });
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<SettingsSuggestOutlinedIcon color="primary" />}
        title={
          <Typography variant="h6">Tanss Einstellungsmöglichkeiten</Typography>
        }
        subheader={`Für ${app?.title || deployment?.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <FormControl fullWidth>
              <InputLabel id="display-name-format-label">
                Verwendetes Format für den Namen in Tanss
              </InputLabel>
              <Select
                labelId="display-name-format-label"
                value={displayNameFormat}
                label="Verwendetes Format für den Namen in Tanss"
                onChange={(e) => setDisplayNameFormat(e.target.value)}
              >
                <MenuItem value="firstName lastName">Vorname Nachname</MenuItem>
                <MenuItem value="lastName, firstName">
                  Nachname, Vorname
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FormControl fullWidth>
              <InputLabel id="tanss-category-label">
                Tanss Kategorie auswählen
              </InputLabel>
              <Select
                labelId="tanss-category-label"
                value={selectedCategoryId}
                label="Tanss Kategorie auswählen"
                onChange={handleCategoryChange}
              >
                {tanssCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedCategoryId && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}
              >
                Zuweisung der Weclapp-Nutzer zu den Tanss Teams
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {mappings.map((mapping, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Box sx={{ flex: 1.5 }}>
                      <UserSelector
                        user={mapping.user}
                        setUser={(user) => updateMapping(index, 'user', user)}
                      />
                    </Box>

                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Tanss Type</InputLabel>
                      <Select
                        value={mapping.value}
                        label="Tanss Type"
                        sx={{ height: '60px' }}
                        onChange={(e) =>
                          updateMapping(index, 'value', e.target.value)
                        }
                      >
                        {availableTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <IconButton
                      color="error"
                      onClick={() => removeMapping(index)}
                      disabled={mappings.length === 1 && !mapping.user}
                      sx={{
                        bgcolor: (theme) =>
                          alpha(theme.palette.error.main, 0.05),
                        '&:hover': {
                          bgcolor: (theme) =>
                            alpha(theme.palette.error.main, 0.1),
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      </CardActions>
    </Card>
  );
};

export default TanssCrmSettingsForm;
